'use client';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useState } from 'react';
import { getCountdownTargetMs, getCountdownBreakdown } from '@/lib/countdown';

function pad(num: number) {
  if (num > 99) return ('0' + num).slice(-3);
  return ('0' + num).slice(-2);
}

interface CountDownProps {
  date?: string;
  gap?: string;
  enableDays?: boolean;
  enableStrings?: boolean;
}

// Componente para renderizar um número com dígitos separados
function NumberGroup({ value }: { value: string }) {
  const digits = value.split('');

  return (
    <div className="flex gap-1.5">
      {digits.map((digit, index) => (
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
          }}
          key={index}
          className="backdrop-blur-[1rem] relative border-gradient flex items-center justify-center w-[4.8rem] h-[5.18rem] lg:w-[5.18rem] lg:h-[6.48rem] rounded-[0.64rem]"
        >
          <div className="font-newsreader font-[400] text-offwhite text-[2.5rem] lg:text-[3.24rem] leading-none">
            {digit}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Countdown({
  date,
  gap = 'gap-[0.65rem]',
  enableDays = false,
  enableStrings = false,
}: CountDownProps) {
  // Target instant (ms): robustly parsed and anchored to Germany time.
  const targetMs = useMemo(() => getCountdownTargetMs(date), [date]);

  // Initialise from the target so the first paint already shows the real time
  // (no 00:00:00:00 flash) instead of zeros.
  const [timeLeft, setTimeLeft] = useState(() =>
    getCountdownBreakdown(targetMs),
  );

  useEffect(() => {
    if (targetMs == null) return;

    const tick = () => setTimeLeft(getCountdownBreakdown(targetMs));

    // Recalcula imediatamente e a cada segundo.
    tick();
    const interval = setInterval(tick, 1000);

    return () => clearInterval(interval);
  }, [targetMs]);

  return (
    <div className="flex flex-wrap items-start gap-[1.3rem]">
      <div className={cn('flex items-center', gap)}>
        {/* Horas */}
        {enableDays && (
          <div className="flex flex-col items-center gap-[0.65rem]">
            <div className={cn('flex items-center', gap)}>
              <NumberGroup value={pad(timeLeft.days)} />
              <Dots />
            </div>

            {enableStrings && (
              <span className="block text-center font-nunito text-[1.375rem] text-white font-normal tracking-[0.45375rem] uppercase">
                Days
              </span>
            )}
          </div>
        )}

        {/* Horas */}
        <div className="flex flex-col items-center gap-[0.65rem]">
          <div className={cn('flex items-center', gap)}>
            <NumberGroup value={pad(timeLeft.hours)} />
            <Dots />
          </div>

          {enableStrings && (
            <span className="block text-center font-nunito text-[1.375rem] text-white font-normal tracking-[0.45375rem] uppercase">
              Hours
            </span>
          )}
        </div>

        {/* Minutos */}
        <div className="flex flex-col items-center gap-[0.65rem]">
          <div className={cn('flex items-center', gap)}>
            <NumberGroup value={pad(timeLeft.minutes)} />
            <Dots />
          </div>

          {enableStrings && (
            <span className="block text-center font-nunito text-[1.375rem] text-white font-normal tracking-[0.45375rem] uppercase">
              Minutes
            </span>
          )}
        </div>
        <div className="flex flex-col items-center gap-[0.65rem]">
          <div className={cn('flex items-center', gap)}>
            <NumberGroup value={pad(timeLeft.seconds)} />
            {/* <Dots /> */}
          </div>

          {enableStrings && (
            <span className="block text-center font-nunito text-[1.375rem] text-white font-normal tracking-[0.45375rem] uppercase">
              Seconds
            </span>
          )}
        </div>
      </div>
      {/* <div
        style={{
          background: 'rgba(255, 255, 255, 0.08)',
        }}
        className="backdrop-blur-[1rem] relative border-gradient flex items-center justify-center w-fit px-[2rem] h-[6.48rem] rounded-[0.64rem]"
      >
        <div className="font-newsreader font-[400] text-offwhite text-[3.24rem] leading-none">
          {formattedDate}
        </div>
      </div> */}
    </div>
  );
}

function Dots() {
  return (
    <svg
      className="w-[0.32rem] h-auto"
      xmlns="http://www.w3.org/2000/svg"
      width="3"
      height="11"
      fill="none"
      viewBox="0 0 3 11"
    >
      <path
        fill="#F2F0EF"
        fillOpacity="0.24"
        d="M1.297 7.662q.57 0 .93.39.368.392.368.968 0 .567-.368.967-.36.391-.93.391-.561 0-.929-.39A1.38 1.38 0 0 1 0 9.02q0-.577.368-.967.377-.391.93-.391m0-7.662q.57 0 .93.39.368.392.368.968 0 .566-.368.968-.36.39-.93.39-.561 0-.929-.39A1.38 1.38 0 0 1 0 1.358Q0 .782.368.391.745 0 1.298 0"
      ></path>
      <path
        stroke="#F2F0EF"
        strokeOpacity="0.24"
        strokeWidth="1.297"
        d="M1.297 8.31c.214 0 .346.065.452.181l.005.006c.123.13.192.29.192.523a.75.75 0 0 1-.111.418l-.086.11c-.106.117-.238.181-.452.181a.57.57 0 0 1-.453-.183.73.73 0 0 1-.196-.526c0-.23.067-.387.187-.517a.6.6 0 0 1 .462-.192Zm0-7.662c.214 0 .346.065.452.181l.005.007a.7.7 0 0 1 .192.522.73.73 0 0 1-.197.529c-.106.117-.238.181-.452.181a.57.57 0 0 1-.453-.184.73.73 0 0 1-.196-.526c0-.229.067-.386.187-.516a.6.6 0 0 1 .462-.194Z"
      ></path>
    </svg>
  );
}
